/**
 * DeliverableBar — the single, shared "Generate deliverables" entry point used by
 * BOTH the Assessment run view and the ENTERPRISE360 blueprint view, so the two
 * tabs look and behave identically. It is the branded card + purple button that
 * opens the shared <DeliverableStudio/>.
 *
 * Props:
 *   label, description     — card heading + blurb
 *   buttonLabel            — CTA text (default "Generate deliverables")
 *   studioTitle, studioSubtitle, groups — passed through to DeliverableStudio
 *   disabled, disabledHint — disable the CTA + show a reason (e.g. "link a client")
 *   notice                 — node rendered above the row (e.g. a low-confidence ack)
 */
import React, { useState } from "react";
import { Sparkles, FileDown } from "lucide-react";
import DeliverableStudio from "./DeliverableStudio";

export default function DeliverableBar({
  label = "Branded deliverables",
  description,
  buttonLabel = "Generate deliverables",
  studioTitle,
  studioSubtitle,
  groups = [],
  disabled = false,
  disabledHint,
  notice = null,
  testId = "deliverable-bar",
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid={testId}>
      {notice && <div className="mb-3">{notice}</div>}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
            <FileDown size={14} /> {label}
          </div>
          {description && <p className="text-[12px] text-zinc-500 mt-1 max-w-md">{description}</p>}
          {disabled && disabledHint && (
            <p className="text-[12px] text-amber-700 mt-2" data-testid="deliverable-bar-disabled-hint">{disabledHint}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={disabled}
          className="shrink-0 inline-flex items-center gap-2 bg-[#7737F6] text-white text-[13px] font-medium px-5 py-2.5 hover:bg-[#5f29c9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="open-deliverable-studio"
        >
          <Sparkles size={15} /> {buttonLabel}
        </button>
      </div>
      <DeliverableStudio
        open={open}
        onClose={() => setOpen(false)}
        title={studioTitle}
        subtitle={studioSubtitle}
        groups={groups}
      />
    </div>
  );
}
